export interface ColorInfo {
  hex: string;
  rgb: { r: number; g: number; b: number };
  isDark: boolean;
}

export async function extractDominantColor(
  imageUrl: string,
): Promise<ColorInfo> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // Only set crossOrigin for external URLs
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Could not get canvas context');
        }

        // Use a smaller size for performance
        const sampleSize = 50;
        canvas.width = sampleSize;
        canvas.height = sampleSize;

        // Draw image scaled down
        ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

        // Get image data
        const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
        const data = imageData.data;

        // Color buckets for dominant color calculation
        const colorMap = new Map<string, number>();

        // Sample every 4th pixel for performance
        for (let i = 0; i < data.length; i += 16) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // Skip transparent pixels
          if (a < 128) continue;

          // Skip very dark colors (< 30) and very light colors (> 225)
          const brightness = (r + g + b) / 3;
          if (brightness < 30 || brightness > 225) continue;

          // Skip grayscale colors (where R, G, B are too similar)
          const maxDiff = Math.max(
            Math.abs(r - g),
            Math.abs(g - b),
            Math.abs(r - b),
          );
          if (maxDiff < 15) continue;

          // Round colors to reduce variations
          const roundedR = Math.round(r / 20) * 20;
          const roundedG = Math.round(g / 20) * 20;
          const roundedB = Math.round(b / 20) * 20;

          const key = `${roundedR},${roundedG},${roundedB}`;
          colorMap.set(key, (colorMap.get(key) || 0) + 1);
        }

        // Find most common color
        let dominantColor = { r: 0, g: 0, b: 0 };
        let maxCount = 0;

        // If we filtered out all colors, fallback to sampling without filters
        if (colorMap.size === 0) {
          // Re-sample without filters
          for (let i = 0; i < data.length; i += 16) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            if (a < 128) continue;

            const roundedR = Math.round(r / 20) * 20;
            const roundedG = Math.round(g / 20) * 20;
            const roundedB = Math.round(b / 20) * 20;

            const key = `${roundedR},${roundedG},${roundedB}`;
            colorMap.set(key, (colorMap.get(key) || 0) + 1);
          }
        }

        for (const [colorKey, count] of colorMap.entries()) {
          if (count > maxCount) {
            maxCount = count;
            const [r, g, b] = colorKey.split(',').map(Number);
            dominantColor = { r, g, b };
          }
        }

        // Enhance saturation for better visual impact
        const enhancedColor = enhanceSaturation(dominantColor);

        // Convert to hex
        const hex = rgbToHex(enhancedColor.r, enhancedColor.g, enhancedColor.b);

        // Determine if color is dark
        const brightness =
          (enhancedColor.r * 299 +
            enhancedColor.g * 587 +
            enhancedColor.b * 114) /
          1000;
        const isDark = brightness < 128;

        resolve({
          hex,
          rgb: enhancedColor,
          isDark,
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for color extraction'));
    };

    img.src = imageUrl;
  });
}

function enhanceSaturation(color: { r: number; g: number; b: number }) {
  // Convert RGB to HSL
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    // Grayscale - return as is
    return color;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  // Enhance saturation by 30%
  const enhancedS = Math.min(1, s * 1.3);

  // Convert back to RGB
  const c = (1 - Math.abs(2 * l - 1)) * enhancedS;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;

  let enhancedR, enhancedG, enhancedB;

  if (h < 1 / 6) {
    enhancedR = c;
    enhancedG = x;
    enhancedB = 0;
  } else if (h < 2 / 6) {
    enhancedR = x;
    enhancedG = c;
    enhancedB = 0;
  } else if (h < 3 / 6) {
    enhancedR = 0;
    enhancedG = c;
    enhancedB = x;
  } else if (h < 4 / 6) {
    enhancedR = 0;
    enhancedG = x;
    enhancedB = c;
  } else if (h < 5 / 6) {
    enhancedR = x;
    enhancedG = 0;
    enhancedB = c;
  } else {
    enhancedR = c;
    enhancedG = 0;
    enhancedB = x;
  }

  return {
    r: Math.round((enhancedR + m) * 255),
    g: Math.round((enhancedG + m) * 255),
    b: Math.round((enhancedB + m) * 255),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
}
